import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard,
  Users,
  AlertTriangle,
  Activity,
  LogOut,
  User,
  FileText,
  AlertOctagon,
  Heart,
  Building2,
} from 'lucide-react';
import { authService, PortalUser } from './api/auth';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { InactivityWarningModal } from './components/InactivityWarningModal';
import { ErrorBoundary } from './components/ErrorBoundary';

// Inactivity timeout configuration (in milliseconds)
const INACTIVITY_WARNING_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const INACTIVITY_LOGOUT_TIMEOUT = 60 * 1000; // 1 minute after warning

// Pages
import Dashboard from './pages/Dashboard';
import UserList from './pages/UserList';
import UserDetail from './pages/UserDetail';
import CrashList from './pages/CrashList';
import CrashDetail from './pages/CrashDetail';
import LiveMonitor from './pages/LiveMonitor';
import DiagnosticsList from './pages/DiagnosticsList';
import FailedSyncs from './pages/FailedSyncs';
import SystemHealth from './pages/SystemHealth';
import TeamList from './pages/TeamList';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is authenticated on mount
    const isAuth = authService.isAuthenticated();
    if (isAuth) {
      setIsAuthenticated(true);
      setUser(authService.getUser());
    }
    setLoading(false);
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    setUser(authService.getUser());
  };

  const handleLogout = useCallback(async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  // Inactivity timeout - only active when authenticated
  const {
    showWarning,
    secondsRemaining,
    dismissWarning,
  } = useInactivityTimeout({
    warningTimeout: INACTIVITY_WARNING_TIMEOUT,
    logoutTimeout: INACTIVITY_LOGOUT_TIMEOUT,
    onLogout: handleLogout,
    isActive: isAuthenticated,
  });

  // Show loading state briefly while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  // Not authenticated - show login/register/verify routes
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated - show main app
  return (
    <>
      {/* Inactivity Warning Modal */}
      <InactivityWarningModal
        isOpen={showWarning}
        secondsRemaining={secondsRemaining}
        onContinue={dismissWarning}
        onLogout={handleLogout}
      />

      <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <h1 className="text-xl font-bold">Locate2u Support</h1>
          <p className="text-sm text-gray-400">Crash Reporting Portal</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <LayoutDashboard size={20} />
                Dashboard
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/users"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <Users size={20} />
                Users
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/teams"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <Building2 size={20} />
                Teams
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/crashes"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <AlertTriangle size={20} />
                Crashes
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/live"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <Activity size={20} />
                Live Monitor
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/diagnostics"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <FileText size={20} />
                Diagnostics
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/failed-syncs"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <AlertOctagon size={20} />
                Failed Syncs
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/system-health"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`
                }
              >
                <Heart size={20} />
                System Health
              </NavLink>
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t border-gray-800">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2 text-gray-300">
              <User size={20} />
              <div className="overflow-hidden">
                <p className="text-sm font-medium truncate">
                  {user.displayName || user.email}
                </p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 w-full text-gray-300 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <LogOut size={20} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/users" element={<UserList />} />
            <Route path="/users/:deviceId" element={<UserDetail />} />
            <Route path="/teams" element={<TeamList />} />
            <Route path="/crashes" element={<CrashList />} />
            <Route path="/crashes/:crashId" element={<CrashDetail />} />
            <Route path="/live" element={<LiveMonitor />} />
            <Route path="/diagnostics" element={<DiagnosticsList />} />
            <Route path="/failed-syncs" element={<FailedSyncs />} />
            <Route path="/system-health" element={<SystemHealth />} />
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route path="/register" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
    </>
  );
}

export default App;
